import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CreditCard, Hourglass, RefreshCw, ShieldCheck, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { ErrorBox, SkeletonPage } from '../ui/kit';
import { datumUndZeit } from '../lib/zeit';
import './Settlements.css';
import PageHeader from '../components/PageHeader';

// STRIPE-AUFSTOCKUNG-ADMIN-001 (Owner 12.09.2026): Admin-Werkbank „Zahlungsabschlüsse".
// Zeigt jede Karten-Einzugszeile, die eine Partnerabrechnung blockiert — mit Fahrt, Partner,
// Kunde, Beträgen, Grund in Klarsprache und der von Stripe belegten Reservierungsfrist.
// Neuversuch und Teileinzug sind getrennte, bestätigungspflichtige Schreibaktionen. Der Server
// entscheidet über ihre Ausführbarkeit; Beträge, Zustände und Fristen werden hier nicht geraten.

const money = (cents) => (Number(cents || 0) / 100).toLocaleString('de-DE', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}) + ' €';

const berlin = (iso) => datumUndZeit(iso, { jahr: 'voll', leer: '–' });

const daysLeft = (iso, now) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (!Number.isFinite(ms)) return null;
  return Math.ceil(ms / 86400000);
};

const shortId = (id) => (id ? String(id).slice(0, 8) : '–');

const statusClass = (status) => {
  if (status === 'blocked') return 'is-blocked';
  if (status === 'submitted') return 'is-submitted';
  return 'is-pending';
};

export default function Settlements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  // Teileinzug: gewählte Kostenträger-Variante. Bewusst KEINE Vorauswahl (Owner-Auftrag 12.09.2026).
  const [variant, setVariant] = useState(null);
  // Owner-Abschluss (Owner 12.10.2026): Pflichtbegründung, bewusst ohne Vorbelegung.
  const [ownerNote, setOwnerNote] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const inFlight = useRef(new Set());
  const reloadTimer = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get('/admin/settlements?limit=200')
      .then((response) => { setData(response.data); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    return () => { if (reloadTimer.current) clearTimeout(reloadTimer.current); };
  }, [load]);

  // Zwei Aktionen, beide nur auf blockierten Zeilen, beide erst nach Bestätigung:
  //  · retry: Neuversuch (nach dem Feld-Fix kann die Aufstockung jetzt gelingen)
  //  · shortfall: Teileinzug — reservierten Betrag einziehen, Rest als Kundenschuld, Partner voll
  //    (Owner 12.09.2026: pro Fall hier zu entscheiden). Beträge kommen vom Server.
  const ACTION_ERRORS = {
    not_blocked: 'Diese Zeile ist nicht mehr blockiert; ein Einzug läuft bereits.',
    not_found: 'Diese Zeile gibt es nicht mehr.',
    not_increase_failure: 'Teileinzug gibt es nur, wenn Stripe die Aufstockung endgültig abgelehnt hat.',
    no_shortfall: 'Für diese Zeile gibt es keinen Rest; der reservierte Betrag deckt den Endpreis.',
    attempt_not_authorized: 'Die Reservierung ist nicht mehr offen; ein Teileinzug ist nicht möglich.',
    attempt_unavailable: 'Der gebundene Stripe-Zahlungsversuch ist nicht vollständig verfügbar.',
    authorization_expiry_unknown: 'Stripe hat keine bestätigte Einzugsfrist geliefert; ein Teileinzug bleibt gesperrt.',
    authorization_expired: 'Die von Stripe bestätigte Einzugsfrist ist abgelaufen.',
    finance_payload_missing: 'Die gebundene Abrechnungsgrundlage fehlt.',
    not_ride_completion: 'Teileinzug ist nur beim Fahrtabschluss möglich.',
    no_payment_review_required: 'Zahlungsbelege haben sich geändert oder sind unklar. Der Nullfall bleibt zur Prüfung offen.',
    no_payment_resolution_required: 'Dieser Nullfall braucht keinen Zahlungsneuversuch. Bitte Nullfall bereinigen wählen.',
    payment_basis_missing: 'Die Zahlungsgrundlage fehlt. Ein Neuversuch kann so nicht ausgeführt werden.',
    payment_state_requires_review: 'Der Zahlungszustand muss zuerst geklärt werden.',
    settlement_not_idle: 'Dieser Vorgang läuft bereits oder wurde anders abgeschlossen. Bitte den aktuellen Stand prüfen.',
    variant_required: 'Bitte zuerst eine Variante wählen.',
    variant_not_allowed: 'Diese Variante gibt es für eine Gebühr nicht. Der Rest wird dort endgültig erlassen.',
    note_required: 'Bitte eine Begründung mit 10 bis 1000 Zeichen eintragen.',
    confirmation_required: 'Der Abschluss braucht eine ausdrückliche Bestätigung.',
    kind_mismatch: 'Der Stand der Zeile hat sich geändert. Bitte die aktualisierte Liste prüfen.',
    already_resolved: 'Diese Zeile wurde bereits abgeschlossen.',
  };

  const runAction = async (row, action, variant = null) => {
    if (inFlight.current.has(row.outbox_id)) return;
    const path = action === 'no-payment' ? 'resolve-no-payment' : action === 'shortfall' ? 'settle-shortfall' : 'retry';
    const plan = action === 'shortfall' ? (row.shortfall_variants || []).find((v) => v.key === variant) : null;
    if (action === 'shortfall' && !plan) {
      toast.error('Bitte zuerst eine Variante wählen.');
      return;
    }
    inFlight.current.add(row.outbox_id); setBusyId(row.outbox_id); setActionResult(null);
    try {
      const response = await api.post(`/admin/settlements/${row.outbox_id}/${path}`, action === 'shortfall' ? { variant } : {});
      if (action === 'no-payment' && (response.data?.ok !== true
          || response.data.resolution !== 'no_payment_required' || response.data.status !== 'settled'))
        throw Error('unconfirmed_no_payment_resolution');
      if (action === 'no-payment') setActionResult({rideId:row.ride_id,text:'Keine Zahlung erforderlich – Nullfall abgeschlossen. Es wurde nichts eingezogen.'});
      toast.success(action === 'no-payment' ? 'Keine Zahlung erforderlich – Nullfall abgeschlossen.' : action === 'shortfall'
        ? `Teileinzug angestoßen (${plan.label}): ${money(row.shortfall_capture_cents)} werden eingezogen, Fehlbetrag ${money(row.shortfall_open_cents)} trägt ${plan.bearer === 'voogo' ? 'VOOGO' : 'der Partner'}${plan.debt ? ', Kundenschuld bleibt offen' : ', keine Kundenschuld'}.`
        : 'Neuversuch vorgemerkt. Das Ergebnis wird vom Backend bestätigt.');
      setConfirmId(null);
      setVariant(null);
      await load();
      // Der Einzug läuft nach dem Commit auf dem Server; ein zweiter Lesezugriff zeigt das Ergebnis.
      if (action !== 'no-payment') reloadTimer.current = setTimeout(() => { load(); }, 4000);
    } catch (error) {
      const code = error.response?.data?.error;
      toast.error(ACTION_ERRORS[code] || error.response?.data?.message
        || (action === 'no-payment' ? 'Nullabschluss konnte nicht bestätigt werden.' : action === 'shortfall' ? 'Teileinzug konnte nicht angestoßen werden' : 'Neuversuch konnte nicht angestoßen werden'));
      await load();
    } finally {
      inFlight.current.delete(row.outbox_id); setBusyId(null);
    }
  };
  // Owner-Abschluss: genau die eine Entscheidung, die der Server für diese Zeile anbietet. Der Server
  // prüft erneut, ob Geld an der Fahrt hängt; ein Erfolg gilt erst mit seiner Bestätigung.
  const ownerClose = async (row) => {
    if (!row.owner_close || inFlight.current.has(row.outbox_id)) return;
    const note = ownerNote.trim();
    if (note.length < 10) { toast.error(ACTION_ERRORS.note_required); return; }
    inFlight.current.add(row.outbox_id); setBusyId(row.outbox_id); setActionResult(null);
    try {
      const response = await api.post(`/admin/settlements/${row.outbox_id}/owner-close`,
        { kind: row.owner_close.kind, note, confirm: true });
      if (response.data?.ok !== true || response.data.status !== 'settled') throw Error('unconfirmed_owner_close');
      const text = row.owner_close.kind === 'fee_waived'
        ? `Gebühr erlassen – nichts eingezogen, nichts gebucht.${response.data.release_pending ? ' Die Kartenreservierung wird freigegeben.' : ''}`
        : 'Ohne Zahlung abgeschlossen. Es wurde nichts eingezogen.';
      setActionResult({ rideId: row.ride_id, text });
      toast.success(text);
      setConfirmId(null); setOwnerNote('');
      await load();
    } catch (error) {
      const code = error.response?.data?.error;
      toast.error(ACTION_ERRORS[code] || error.response?.data?.message || 'Abschluss konnte nicht bestätigt werden.');
      await load();
    } finally {
      inFlight.current.delete(row.outbox_id); setBusyId(null);
    }
  };
  const retry = (row) => runAction(row, 'retry');
  const settleShortfall = (row) => {
    if (!row.can_settle_shortfall) return;
    return runAction(row, 'shortfall', variant);
  };

  if (loading && !data) return <SkeletonPage />;
  if (loadError && !data) return <ErrorBox onRetry={load} />;

  const counts = data?.counts || {};
  const rows = data?.rows || [];
  const now = Date.now();

  return (
    <div className="settlements-page">
      <PageHeader
        title="Zahlungsabschlüsse"
        description="Einzüge, Freigaben und Nullfälle, die noch geprüft oder abgeschlossen werden müssen."
        actions={(
          <div className="settlements-head-actions">
            <span className="settlements-source"><ShieldCheck size={12} /> Backend-verifiziert</span>
            <button type="button" className="settlements-reload" onClick={load} disabled={loading}>
              <RefreshCw size={13} /> Aktualisieren
            </button>
          </div>
        )}
      />

      {actionResult && <div role="status" className="settlements-note"><ShieldCheck size={15} /><span>{shortId(actionResult.rideId)}: {actionResult.text}</span></div>}

      <section className="settlements-kpis" aria-label="Übersicht">
        <article className={counts.blocked > 0 ? 'is-alert' : ''}>
          <span className="settlements-overline">Blockiert</span>
          <strong>{counts.blocked || 0}</strong>
          <small>kein automatischer Neuversuch</small>
        </article>
        <article>
          <span className="settlements-overline">Wartet / läuft</span>
          <strong>{(counts.pending || 0) + (counts.sending || 0)}</strong>
          <small>wird automatisch erneut versucht</small>
        </article>
        <article>
          <span className="settlements-overline">Bestätigung offen</span>
          <strong>{counts.submitted || 0}</strong>
          <small>an Stripe übergeben</small>
        </article>
        <article>
          <span className="settlements-overline">Offen gesamt</span>
          <strong>{counts.total || 0}</strong>
          <small>Zeilen mit Partner sperren dessen Abrechnung</small>
        </article>
      </section>

      <section className="settlements-note">
        <span className="settlements-section-icon"><Hourglass size={15} /></span>
        <div>
          <strong>Was hier steht</strong>
          <p>
            Die Aktion je Zeile richtet sich nach den bestätigten Zahlungsbelegen: Einzug erneut
            versuchen, eine Autorisierung freigeben oder einen eindeutigen Nullfall ohne Zahlung
            abschließen. Fehlende oder widersprüchliche Belege erfordern Prüfung. Ein vorgemerkter
            Neuversuch ist noch kein Erfolg. Fristen stammen aus dem vorhandenen Providerbeleg.
            Blockierte Zeilen ohne Geld an der Fahrt kann der Owner mit Begründung selbst abschließen;
            eine Gebühr lässt sich erlassen oder bis zum reservierten Betrag einziehen.
          </p>
        </div>
      </section>

      <section className="settlements-table-section">
        <div className="settlements-section-heading">
          <span className="settlements-section-icon"><CreditCard size={15} /></span>
          <div>
            <span className="settlements-overline">Offene Zeilen</span>
            <h2>Einzüge je Fahrt</h2>
          </div>
        </div>
        <div className="settlements-table-wrap">
          <table>
            <thead>
              <tr>
                {['Fahrt', 'Partner', 'Kunde', 'Zahlweg', 'Reserviert', 'Fällig', 'Status', 'Grund', 'Reservierung gültig bis', 'Aktion']
                  .map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="settlements-empty-row">
                  <td colSpan="10">Keine offenen Zahlungsabschlüsse.</td>
                </tr>
              ) : rows.map((row) => {
                const left = daysLeft(row.hold_expires_at, now);
                const shortfall = Number(row.amount_cents || 0) - Number(row.authorized_cents || 0);
                const [confirmRowId, confirmAction] = String(confirmId || '').split(':');
                const isOwnerClosePanel = confirmRowId === String(row.outbox_id) && confirmAction === 'owner-close';
                const isConfirming = confirmRowId === String(row.outbox_id) && !isOwnerClosePanel;
                const isBusy = busyId === row.outbox_id;
                const hasShortfallOptions = (row.shortfall_variants || []).length > 0;
                const isShortfallPanel = confirmAction === 'shortfall' || confirmAction === 'shortfall-blocked';
                const shortfallLocked = isShortfallPanel && !row.can_settle_shortfall;
                return (
                  <tr key={row.outbox_id} className={row.status === 'blocked' ? 'is-blocked-row' : ''}>
                    <td>
                      <span className="settlements-ride">
                        <strong>{row.settlement_event_label}</strong>
                        <small title={row.ride_id}>{shortId(row.ride_id)} · {berlin(row.finance_occurred_at)}</small>
                      </span>
                    </td>
                    <td><span className="settlements-cell-strong">{row.partner_name || row.partner_id || '–'}</span></td>
                    <td>
                      <span className="settlements-ride">
                        <strong>{row.customer_name || '–'}</strong>
                        <small>{row.customer_email || row.customer_id || ''}</small>
                      </span>
                    </td>
                    <td>
                      <span className="settlements-method">
                        {row.is_wallet_payment ? <Smartphone size={13} /> : <CreditCard size={13} />}
                        {row.is_wallet_payment ? 'Wallet' : 'Karte'}
                        {row.provider_intent_id && <small title={row.provider_intent_id}>{shortId(row.provider_intent_id)}…</small>}
                      </span>
                    </td>
                    <td>{row.authorized_cents == null ? '–' : money(row.authorized_cents)}</td>
                    <td>
                      <span className="settlements-ride">
                        <strong>{money(row.amount_cents)}</strong>
                        {shortfall > 0 && <small className="is-shortfall">Mehrpreis {money(shortfall)}</small>}
                      </span>
                    </td>
                    <td><span className={`settlements-status ${statusClass(row.status)}`}>{row.status_label}</span></td>
                    <td className="settlements-reason">
                      {row.shortfall_variant_label ? (
                        <span className="settlements-ride is-shortfall-detail">
                          <strong>{row.shortfall_variant_label}</strong>
                          <small>Einzug {money(row.shortfall_capture_cents)} · Fehlbetrag {money(row.shortfall_open_cents)}</small>
                          <small>Kostenträger {row.shortfall_bearer === 'voogo' ? 'VOOGO' : 'Partner'} · {row.shortfall_debt ? `Kundenschuld ${money(row.shortfall_open_cents)}` : 'keine Kundenschuld'}</small>
                          {row.error_label && <small>Status: {row.error_label}</small>}
                        </span>
                      ) : (row.reason_label || row.no_payment_review_label || row.error_label || '–')}
                    </td>
                    <td>
                      {row.hold_expires_at ? (
                        <span className={`settlements-ride ${left != null && left <= 2 ? 'is-urgent' : ''}`}>
                          <strong>{berlin(row.hold_expires_at)}</strong>
                          <small>{left == null ? 'laut Stripe' : left < 0 ? 'laut Stripe verfallen' : left === 0 ? 'heute, laut Stripe' : `noch ${left} ${left === 1 ? 'Tag' : 'Tage'}, laut Stripe`}</small>
                        </span>
                      ) : (
                        <span className="settlements-ride">
                          <strong>unbekannt</strong>
                          <small>{['authorization_pending', 'authorized'].includes(row.attempt_state) ? 'Stripe hat keine Frist genannt' : 'keine offene Reservierung'}</small>
                        </span>
                      )}
                    </td>
                    <td>
                      {row.can_resolve_no_payment ? (
                        <span className="settlements-actions">
                          {isConfirming ? <>
                            <small>Nullfall erneut prüfen und ohne Einzug abschließen. Kein Forderungserlass.</small>
                            <button type="button" className="is-primary" disabled={isBusy} onClick={() => runAction(row,'no-payment')}>{isBusy ? 'Läuft…' : 'Nullfall abschließen'}</button>
                            <button type="button" disabled={isBusy} onClick={() => setConfirmId(null)}>Abbrechen</button>
                          </> : <button type="button" onClick={() => setConfirmId(`${row.outbox_id}:no-payment`)}>Nullfall bereinigen</button>}
                        </span>
                      ) : (row.can_retry || hasShortfallOptions) ? (
                        isConfirming ? (
                          <span className="settlements-actions is-confirm">
                            {isShortfallPanel ? (
                              <>
                                <small>
                                  {shortfallLocked
                                    ? `Diese ${(row.shortfall_variants || []).length === 4 ? 'vier ' : ''}Varianten stehen nach einem gültigen Stripe-Nachweis zur Verfügung.`
                                    : 'Wer trägt den Fehlbetrag? Bitte eine Variante wählen.'}
                                </small>
                                {shortfallLocked && (
                                  <span className="settlements-shortfall-block" role="status">
                                    <AlertCircle size={14} />
                                    <span>
                                      <strong>Teileinzug derzeit gesperrt</strong>
                                      <small>{row.shortfall_blocked_label}</small>
                                    </span>
                                  </span>
                                )}
                                <span className="settlements-variants" role="radiogroup" aria-label="Kostenträger-Variante">
                                  {(row.shortfall_variants || []).map((option) => (
                                    <label key={option.key} className={`${variant === option.key ? 'is-selected' : ''} ${shortfallLocked ? 'is-disabled' : ''}`.trim()}>
                                      <input type="radio" name={`variant-${row.outbox_id}`} value={option.key}
                                        checked={variant === option.key} disabled={shortfallLocked}
                                        onChange={() => setVariant(option.key)} />
                                      <strong>{option.label}</strong>
                                      <small>{option.description}</small>
                                    </label>
                                  ))}
                                </span>
                                {(() => {
                                  const plan = (row.shortfall_variants || []).find((v) => v.key === variant);
                                  return (
                                    <span className="settlements-summary">
                                      <span><b>Einzug</b> {money(row.shortfall_capture_cents)}</span>
                                      <span><b>Fehlbetrag</b> {money(row.shortfall_open_cents)}</span>
                                      {plan && <span><b>Kostenträger</b> {plan.bearer === 'voogo' ? 'VOOGO' : 'Partner'}</span>}
                                      {plan && <span><b>Kundenschuld</b> {plan.debt ? money(row.shortfall_open_cents) : 'keine'}</span>}
                                    </span>
                                  );
                                })()}
                                {!shortfallLocked && !variant && (
                                  <small className="settlements-summary is-empty">Noch keine Variante gewählt.</small>
                                )}
                              </>
                            ) : (
                              <small>Die bestätigte Zahlungsaktion erneut vormerken. Ein Erfolg ist erst nach der Backendbestätigung belegt.</small>
                            )}
                            <span className="settlements-actions">
                              <button type="button" className="is-primary"
                                disabled={isBusy || shortfallLocked || (confirmAction === 'shortfall' && !variant)}
                                onClick={() => (isShortfallPanel ? settleShortfall(row) : retry(row))}>
                                {shortfallLocked ? 'Teileinzug gesperrt' : isBusy ? 'Läuft…' : 'Jetzt anstoßen'}
                              </button>
                              <button type="button" disabled={isBusy} onClick={() => { setConfirmId(null); setVariant(null); }}>Abbrechen</button>
                            </span>
                          </span>
                        ) : (
                          <span className="settlements-actions">
                            {row.can_retry && <button type="button" onClick={() => { setVariant(null); setConfirmId(`${row.outbox_id}:retry`); }}>{row.action_label || 'Erneut versuchen'}</button>}
                            {hasShortfallOptions && (
                              // Review P3-4: jede Zeile beginnt ohne Variante — keine Vorauswahl durch die Hintertür.
                              <button type="button" className="is-shortfall" onClick={() => {
                                setVariant(null);
                                setConfirmId(`${row.outbox_id}:${row.can_settle_shortfall ? 'shortfall' : 'shortfall-blocked'}`);
                              }}>
                                {row.can_settle_shortfall
                                  ? 'Reservierten Betrag einziehen, Fehlbetrag zuordnen'
                                  : 'Fehlbetrag-Optionen ansehen (gesperrt)'}
                              </button>
                            )}
                          </span>
                        )
                      ) : (
                        row.status==='blocked' ? <details><summary>Zahlungsgrundlage prüfen</summary>
                          <p>{row.no_payment_review_label || 'Zahlungsnachweise und gebundenes Originalangebot zuerst abgleichen.'}</p>
                          <p>Fahrt: {row.ride_id}. Vorgang: {row.outbox_id}. Prüfgrund: {row.no_payment_review_reason || row.retry_blocked_reason || 'nicht eindeutig'}.</p>
                          <p>Die angezeigten 0,00 € sind der Betrag dieses Freigabevorgangs. Sie belegen keine fehlende Forderung. Fahrt im Auftragsverlauf öffnen und Originalangebot, Storno sowie Zahlungsversuche abgleichen. Danach Liste aktualisieren.</p>
                          {!row.owner_close && row.owner_close_blocked_label && <p>Kein Owner-Abschluss: {row.owner_close_blocked_label}</p>}
                        </details> : <span className="settlements-auto">automatisch</span>
                      )}
                      {row.owner_close && (isOwnerClosePanel ? (
                        <span className="settlements-actions is-confirm settlements-owner-close">
                          <strong>{row.owner_close.label}</strong>
                          <small>{row.owner_close.description}</small>
                          {row.owner_close.kind === 'fee_waived' && (
                            <small>Gebühr {money(row.amount_cents)} entfällt. Betrag und Abrechnungsgrundlage bleiben als Nachweis gespeichert.</small>
                          )}
                          <label className="settlements-owner-note">
                            <span>Begründung (Pflicht, mindestens 10 Zeichen)</span>
                            <textarea rows={3} maxLength={1000} value={ownerNote} disabled={isBusy}
                              aria-label={`Begründung für ${row.owner_close.label}`}
                              onChange={(event) => setOwnerNote(event.target.value)} />
                          </label>
                          <small>Der Abschluss ist endgültig und wird mit Namen, Zeitpunkt und Begründung protokolliert.</small>
                          <span className="settlements-actions">
                            <button type="button" className="is-primary" disabled={isBusy || ownerNote.trim().length < 10}
                              onClick={() => ownerClose(row)}>{isBusy ? 'Läuft…' : 'Endgültig abschließen'}</button>
                            <button type="button" disabled={isBusy} onClick={() => { setConfirmId(null); setOwnerNote(''); }}>Abbrechen</button>
                          </span>
                        </span>
                      ) : !isConfirming && (
                        <span className="settlements-actions settlements-owner-close">
                          <button type="button" className="is-owner-close" onClick={() => {
                            setVariant(null); setOwnerNote(''); setConfirmId(`${row.outbox_id}:owner-close`);
                          }}>{row.owner_close.label}</button>
                        </span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
